import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import InfoSection from './Form2/InfoSection';
import CharacteristicsSection from './Form2/CharacteristicsSection';
import YarusySection from './Form2/YarusySection';
import PorodaCharacteristicsSection from './Form2/PorodaCharacteristicsSection';
import PickerModal from './Form2/PickerModal';
import YearPickerModal from './Form2/YearPickerModal';
import CompositionModal from './Form2/CompositionModal';
import { getEmptyFormData } from './Form2/constants';
import { usePicker } from './Form2/pickerHooks';
import { useFormValidation, useClearTableData } from './Form2/formHooks';
import { isNonForestLandCategory } from './Form2/utils';
import { mobilePlotArea } from '../../utils/mobileCalculations';
import { doubleOrNil } from '../../utils/mpOchags';

const Form2 = ({ handbooks, formData, setFormData, selectedDoc, dataType, independentData, setIndependentData, form1Data, form4Data, form5Data }) => {
  
  // Определяем, должны ли поля быть заблокированы
  // По умолчанию поля разблокированы для ввода данных
  const isFieldsLocked = false;
  

  // Используем formData из props напрямую
  const displayData = formData || getEmptyFormData();
  const setLocalFormData = setFormData;

  // Состояние для активного яруса при выборе из справочника
  const [activeYarusId, setActiveYarusId] = useState(null);

  // Состояние для календаря года
  const [showYearPicker, setShowYearPicker] = useState(false);

  // Состояние для валидации
  const [hasValidationErrors, setHasValidationErrors] = useState(false);

  // Состояние для модального окна состава насаждения
  const [showCompositionModal, setShowCompositionModal] = useState(false);
  const [currentYarusId, setCurrentYarusId] = useState(null);
  const [compositionItems, setCompositionItems] = useState([]);
  
  // Состояние для уведомления
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');

  // Состояние для слайдера таблицы информации
  const [infoTableScrollLeft, setInfoTableScrollLeft] = useState(0);
  const infoTableScrollRef = useRef(null);

  // Функция для обработки прокрутки таблицы информации
  const handleInfoTableScroll = (e) => {
    const scrollLeft = e.target.scrollLeft;
    const maxScroll = e.target.scrollWidth - e.target.clientWidth;
    const scrollPercent = maxScroll > 0 ? (scrollLeft / maxScroll) * 100 : 0;
    setInfoTableScrollLeft(scrollPercent);
  };

  // Используем независимые данные для ярусов (только независимые поля)
  const yarusy = useMemo(() => independentData?.yarusy || [{ 
    id: 1, 
    yarus: '1',
    sostav: ''
  }], [independentData?.yarusy]);

  // Используем независимые зависимые данные для ярусов (одинаковые для ТО и фактических данных)
  const yarusyDependent = useMemo(() => independentData?.yarusy_dependent || [], [independentData?.yarusy_dependent]);

  // Используем независимые данные для характеристик пород (только независимые поля)
  const porodaCharacteristics = useMemo(() => independentData?.poroda_characteristics || [], [independentData?.poroda_characteristics]);

  // Используем независимые зависимые данные для характеристик пород (одинаковые для ТО и фактических данных)
  const porodaCharacteristicsDependent = useMemo(() => independentData?.poroda_characteristics_dependent || [], [independentData?.poroda_characteristics_dependent]);

  // Функции для обновления независимых данных
  const setYarusy = useCallback((newYarusy) => {
    setIndependentData(prev => ({
      ...prev,
      yarusy: newYarusy
    }));
  }, [setIndependentData]);

  const setPorodaCharacteristics = useCallback((newCharacteristics) => {
    setIndependentData(prev => ({
      ...prev,
      poroda_characteristics: newCharacteristics
    }));
  }, [setIndependentData]);

  // Функции для работы с зависимыми данными ярусов
  const setYarusyDependent = useCallback((newYarusyDependent) => {
    setIndependentData(prev => ({
      ...prev,
      yarusy_dependent: newYarusyDependent
    }));
  }, [setIndependentData]);

  // Функции для работы с зависимыми данными характеристик пород
  const setPorodaCharacteristicsDependent = useCallback((newPorodaCharacteristicsDependent) => {
    setIndependentData(prev => ({
      ...prev,
      poroda_characteristics_dependent: newPorodaCharacteristicsDependent
    }));
  }, [setIndependentData]);

  // Функция для объединения независимых и зависимых данных ярусов
  const getCombinedYarusy = useCallback(() => {
    return yarusy.map(yarus => {
      const dependentData = yarusyDependent.find(dep => dep.id === yarus.id);
      return {
        ...yarus,
        visota: dependentData?.visota || '',
        klvozr: dependentData?.klvozr || '',
        grvozr: dependentData?.grvozr || '',
        polnota: dependentData?.polnota || '',
        zapassirga: dependentData?.zapassirga || '',
        zapassir: dependentData?.zapassir || '',
        zapassuh: dependentData?.zapassuh || '',
        zapasred: dependentData?.zapasred || '',
        zapased: dependentData?.zapased || '',
        zapaszahl: dependentData?.zapaszahl || '',
        zapaszahllik: dependentData?.zapaszahllik || ''
      };
    });
  }, [yarusy, yarusyDependent]);

  // Функция для получения damageSigns для характеристики породы
  const getDamageSignsForCharacteristic = useCallback((char) => {
    if (!form4Data && !form5Data) return [];
    
    // Ищем соответствующий элемент в Form4 или Form5 по yarus и kodporoda
    const form4Item = form4Data?.data?.find(item => 
      (String(item.yarus) === String(char.yarus) || item.yarus === char.yarus) &&
      (item.kodporoda === char.kodporodato || item.kodporoda === char.kodporoda)
    );
    
    const form5Item = form5Data?.data?.find(item => 
      (String(item.yarus) === String(char.yarus) || item.yarus === char.yarus) &&
      (item.kodporoda === char.kodporodato || item.kodporoda === char.kodporoda)
    );
    
    // Получаем damageSigns из savedModalData
    const itemId = form4Item?.id || form5Item?.id;
    const savedModalData = form4Data?.savedModalData || form5Data?.savedModalData || {};
    const modalData = itemId ? (savedModalData[itemId] || []) : [];
    
    return modalData;
  }, [form4Data, form5Data]);

  // Функция для объединения независимых и зависимых данных характеристик пород
  const getCombinedPorodaCharacteristics = useCallback(() => {
    return porodaCharacteristics.map(char => {
      const dependentData = porodaCharacteristicsDependent.find(dep => dep.id === char.id);
      const damageSigns = getDamageSignsForCharacteristic(char);

      return {
        ...char,
        // Добавляем dolap из зависимых данных
        dolap: dependentData?.dolap || '',
        // Имена полей те же, что и в API (form2.plantationByTiers[].sostav[]):
        // значение нигде по пути UI → стейт → запрос не переименовывается.
        vozr: dependentData?.vozr || '',
        hsr: dependentData?.hsr || '',
        dsr: dependentData?.dsr || '',
        damageSigns: damageSigns
      };
    });
  }, [porodaCharacteristics, porodaCharacteristicsDependent, getDamageSignsForCharacteristic]);

  // Парсинг строки состава в массив элементов (нужен для rebuildPorodaCharacteristicsFromAllYarusy)
  const parseCompositionString = useCallback((compositionString) => {
    if (!compositionString) return [{ id: 1, dolap: '', kodporodato: '' }];
    
    const items = [];
    const regex = /(\d+|\+)([А-Яа-я]+)/g;
    let match;
    let id = 1;
    
    while ((match = regex.exec(compositionString)) !== null) {
      const porodaName = match[2];
      // Находим ID породы по названию (ищем как с заглавной, так и с любой регистром)
      const handbook = handbooks.find(h => h.id === 31);
      let porodaId = porodaName;
      if (handbook && handbook.references) {
        // Ищем точное совпадение
        let selectedOption = handbook.references.find(r => r.name === porodaName);
        // Если не найдено, ищем без учета регистра
        if (!selectedOption) {
          selectedOption = handbook.references.find(r => 
            r.name.toLowerCase() === porodaName.toLowerCase()
          );
        }
        if (selectedOption) {
          porodaId = selectedOption.id.toString();
        }
      }
      
      items.push({
        id: id++,
        dolap: match[1],
        kodporodato: porodaId
      });
    }
    
    return items.length > 0 ? items : [{ id: 1, dolap: '', kodporodato: '' }];
  }, [handbooks]);

  // Состав яруса с выбранными породами. Как в мобильном приложении, порода —
  // это ID записи справочника, сохранённый при выборе (yarus.sostavItems).
  // По букве из строки состава её не восстановить: за «К» в справочнике семь
  // пород, и разбор строки взял бы первую — сосну кедровую корейскую вместо
  // выбранного кедра сибирского. Строку разбираем только у ярусов без
  // sostavItems (черновики прежней версии) и тогда сверяем буквы с породами,
  // уже известными для яруса.
  const compositionItemsOf = useCallback((yarus) => {
    if (!yarus || !yarus.sostav) return [];
    if (Array.isArray(yarus.sostavItems) && yarus.sostavItems.length > 0) {
      return yarus.sostavItems.map((item, index) => ({
        id: index + 1,
        dolap: String(item.dolap ?? ''),
        kodporodato: String(item.kodporodato ?? '')
      }));
    }
    const handbook = handbooks.find(h => h.id === 31);
    const letterOf = (porodaId) => {
      const record = handbook?.references?.find(r => r.id.toString() === String(porodaId));
      return record ? record.name : String(porodaId || '');
    };
    const known = (porodaCharacteristics || []).filter(char =>
      char.yarusId === yarus.id || String(char.yarus) === String(yarus.yarus)
    );
    const used = new Set();
    return parseCompositionString(yarus.sostav).map(item => {
      const match = known.find(char =>
        !used.has(char) &&
        String(char.dolap) === String(item.dolap) &&
        letterOf(char.kodporodato).toLowerCase() === letterOf(item.kodporodato).toLowerCase()
      );
      if (!match) return item;
      used.add(match);
      return { ...item, kodporodato: String(match.kodporodato) };
    });
  }, [handbooks, parseCompositionString, porodaCharacteristics]);

  // Полное обновление таблицы характеристик пород на основе всех ярусов
  const rebuildPorodaCharacteristicsFromAllYarusy = useCallback(() => {
    const handbook = handbooks.find(h => h.id === 31);
    const allCharacteristics = [];
    const existingCharacteristics = porodaCharacteristics || [];

    const combinedYarusy = getCombinedYarusy();
    
    combinedYarusy.forEach(yarus => {
      if (yarus.sostav) {
        // Выбранные породы яруса (по ID записи справочника, см. compositionItemsOf)
        const compositionItems = compositionItemsOf(yarus);
        const validItems = compositionItems.filter(item => item.dolap && item.kodporodato);
        
        validItems.forEach((item, index) => {
          // kodporodato везде хранится как ID породы (строка) — и здесь, и при
          // загрузке карточки с сервера. Название нужно только для того, чтобы
          // опознать строки, сохранённые старой версией (там лежало имя).
          const porodaId = item.kodporodato;
          let porodaName = porodaId;
          if (handbook && handbook.references) {
            const selectedOption = handbook.references.find(r => r.id.toString() === porodaId);
            if (selectedOption) {
              porodaName = selectedOption.name;
            }
          }

          // Ищем существующую характеристику по ярусу, доле и породе. Ярус
          // сверяем и по yarusId, и по номеру: у строк, пришедших с сервера,
          // yarusId может отсутствовать, и тогда совпадение по нему невозможно.
          const existingChar = existingCharacteristics.find(char =>
            (char.yarusId === yarus.id || String(char.yarus) === String(yarus.yarus)) &&
            char.dolap === item.dolap &&
            (char.kodporodato === porodaId || char.kodporodato === porodaName)
          );

          if (existingChar) {
            // Обновляем существующую характеристику. Ключевое — сохранить её id:
            // по нему находятся зависимые данные (ср. возраст/высота/диаметр),
            // и если выдать строке новый id, введённые значения потеряются.
            allCharacteristics.push({
              ...existingChar,
              yarus: yarus.yarus, // Обновляем ярус
              yarusId: yarus.id, // Сохраняем ID яруса
              dolap: item.dolap,
              kodporodato: porodaId
            });
          } else {
            // Создаем новую характеристику
            allCharacteristics.push({
              id: Date.now() + Math.random() + index, // Уникальный ID
              yarus: yarus.yarus,
              yarusId: yarus.id, // Сохраняем ID яруса
              dolap: item.dolap,
              kodporodato: porodaId
            });
          }
        });
      }
    });

    // Не создаем пустую строку, если нет характеристик

    setPorodaCharacteristics(allCharacteristics);
    
    // Создаем соответствующие зависимые данные
    const allCharacteristicsDependent = allCharacteristics.map(char => {
      const existingDependent = porodaCharacteristicsDependent.find(dep => dep.id === char.id);

      return {
        id: char.id,
        yarus: char.yarus,     // Добавляем ярус
        dolap: char.dolap,     // Добавляем долю
        kodporodato: char.kodporodato,   // Добавляем породу
        vozr: existingDependent?.vozr || '',
        hsr: existingDependent?.hsr || '',
        dsr: existingDependent?.dsr || ''
      };
    });
    
    setPorodaCharacteristicsDependent(allCharacteristicsDependent);
  }, [getCombinedYarusy, handbooks, compositionItemsOf, porodaCharacteristics, setPorodaCharacteristics, porodaCharacteristicsDependent, setPorodaCharacteristicsDependent]);

  // Состояние для слайдера таблицы
  const [tableScrollPosition, setTableScrollPosition] = useState(0);
  const tableScrollRef = useRef(null);

  // Функции для работы со слайдером таблицы
  const handleSliderChange = (e) => {
    const newPosition = parseInt(e.target.value);
    setTableScrollPosition(newPosition);
    if (tableScrollRef.current) {
      const maxScroll = tableScrollRef.current.scrollWidth - tableScrollRef.current.clientWidth;
      const scrollLeft = (newPosition / 100) * maxScroll;
      tableScrollRef.current.scrollLeft = scrollLeft;
    }
  };

  const handleTableScroll = () => {
    if (tableScrollRef.current) {
      const maxScroll = tableScrollRef.current.scrollWidth - tableScrollRef.current.clientWidth;
      const currentScroll = tableScrollRef.current.scrollLeft;
      const percentage = (currentScroll / maxScroll) * 100;
      setTableScrollPosition(percentage);
    }
  };


  // Эффект для прокрутки к текущему году при открытии календаря
  useEffect(() => {
    if (showYearPicker) {
      const timer = setTimeout(() => {
        const currentYear = new Date().getFullYear();
        const years = generateYears();
        const currentYearIndex = years.findIndex(year => year === currentYear);
        
        if (currentYearIndex >= 0) {
          const currentYearElement = document.querySelector(`.year-picker-list li:nth-child(${currentYearIndex + 1})`);
          if (currentYearElement) {
            currentYearElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showYearPicker]);

  // Используем хук для валидации формы
  useFormValidation(
    formData,
    displayData,
    setLocalFormData,
    setFormData,
    getCombinedYarusy,
    hasValidationErrors,
    setHasValidationErrors,
    handbooks
  );

  // Флаг для отслеживания, когда мы сами обновляем данные
  const isUpdatingFromForm = useRef(false);
  
  // Ref для отслеживания предыдущего состава ярусов (чтобы не пересобирать без изменений)
  const prevSostavRef = useRef('');
  // Ref для хранения актуальной версии функции пересборки
  const rebuildPorodaCharacteristicsRef = useRef(null);

  // Обновляем ref при изменении функции пересборки
  useEffect(() => {
    rebuildPorodaCharacteristicsRef.current = rebuildPorodaCharacteristicsFromAllYarusy;
  }, [rebuildPorodaCharacteristicsFromAllYarusy]);

  // Автоматическая пересборка таблицы характеристик пород при изменении состава ярусов
  useEffect(() => {
    const currentSostav = getCombinedYarusy()
      .map(y => y.sostav || '')
      .join('|');
    
    // Пересобираем только если состав действительно изменился
    if (currentSostav !== prevSostavRef.current) {
      prevSostavRef.current = currentSostav;
      // Небольшая задержка, чтобы состояние успело обновиться
      const timer = setTimeout(() => {
        if (rebuildPorodaCharacteristicsRef.current) {
          rebuildPorodaCharacteristicsRef.current();
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [yarusy, yarusyDependent, getCombinedYarusy]);

  // Главная порода всегда равна первой породе первого яруса
  useEffect(() => {
    const firstYarus = getCombinedYarusy().find(y => String(y.yarus) === '1');
    const firstCompositionItem = compositionItemsOf(firstYarus)[0] || null;
    const nextMainSpecies = firstCompositionItem?.kodporodato || '';

    // Пустой расчёт не стираем: состав может быть ещё не разобран (справочник
    // пород не загрузился), и затирать уже сохранённую главную породу нельзя —
    // мобильное приложение считает это поле обязательным.
    if (!nextMainSpecies) {
      return;
    }

    if ((displayData.gporoda || '') !== nextMainSpecies) {
      const newData = {
        ...displayData,
        gporoda: nextMainSpecies
      };
      setLocalFormData(newData);
      if (setFormData) {
        setFormData(newData);
      }
    }
  }, [displayData, getCombinedYarusy, compositionItemsOf, setFormData, setLocalFormData]);

  // Используем хук для очистки данных таблицы
  const clearTableData = useClearTableData(
    displayData,
    setLocalFormData,
    setFormData,
    yarusy,
    setYarusy,
    yarusyDependent,
    setYarusyDependent
  );

  // Функция для показа уведомления
  const showNotificationMessage = (message) => {
    setNotificationMessage(message);
    setShowNotification(true);
    setTimeout(() => {
      setShowNotification(false);
    }, 3000);
  };

  // Функции для работы с модальным окном состава насаждения
  const openCompositionModal = (yarusId) => {
    setCurrentYarusId(yarusId);
    const yarus = getCombinedYarusy().find(y => y.id === yarusId);
    
    // Проверяем, есть ли уже состав
    if (yarus && yarus.sostav) {
      showNotificationMessage('Состав насаждений уже указан и не подлежит изменению. Удалите строку яруса (кнопка "Удалить") и создайте заново');
      return;
    }
    
    if (yarus) {
      setCompositionItems([{ id: 1, dolap: '', kodporodato: '' }]);
    }
    setShowCompositionModal(true);
  };

  const closeCompositionModal = () => {
    setShowCompositionModal(false);
    setCurrentYarusId(null);
    setCompositionItems([]);
  };



  // Преобразование массива элементов в строку состава
  const compositionToString = (items) => {
    return items
      .filter(item => item.dolap && item.kodporodato)
      .map(item => {
        // Получаем название породы по ID
        const handbook = handbooks.find(h => h.id === 31);
        let porodaName = item.kodporodato;
        if (handbook && handbook.references) {
          const selectedOption = handbook.references.find(r => r.id.toString() === item.kodporodato);
          if (selectedOption) {
            porodaName = selectedOption.name;
          }
        }
        
        // Форматируем название породы: первая буква заглавная, остальные строчные
        const formattedPoroda = porodaName.charAt(0).toUpperCase() + porodaName.slice(1).toLowerCase();
        
        return `${item.dolap}${formattedPoroda}`;
      })
      .join('');
  };

  // Добавление нового элемента состава
  const addCompositionItem = () => {
    const newId = Math.max(...compositionItems.map(item => item.id), 0) + 1;
    setCompositionItems([...compositionItems, { id: newId, dolya: '', poroda: '' }]);
  };

  // Удаление элемента состава
  const removeCompositionItem = (itemId) => {
    if (compositionItems.length > 1) {
      setCompositionItems(compositionItems.filter(item => item.id !== itemId));
    }
  };

  // Обновление элемента состава
  const updateCompositionItem = (itemId, field, value) => {
    setCompositionItems(compositionItems.map(item => 
      item.id === itemId ? { ...item, [field]: value } : item
    ));
  };

  // Используем хук для работы с picker
  const {
    showPicker,
    pickerField,
    pickerOptions,
    pickerValue,
    pickerSearch,
    selectedOptionIndex,
    openPicker,
    closePicker,
    selectPickerValue,
    getFilteredOptions,
    getSelectedIndexInFiltered,
    setPickerSearch
  } = usePicker(
    handbooks,
    displayData,
    setLocalFormData,
    setFormData,
    getCombinedYarusy,
    yarusyDependent,
    setYarusyDependent,
    compositionItems,
    updateCompositionItem,
    setActiveYarusId
  );

  // Функция для вычисления суммы долей (исключая "+")
  const calculateDolyaSum = () => {
    return compositionItems
      .filter(item => item.dolap && item.dolap !== '+')
      .reduce((sum, item) => sum + parseInt(item.dolap || 0), 0);
  };

  // Функция для проверки валидности долей
  const isDolyaSumValid = () => {
    return calculateDolyaSum() === 10;
  };

  // Функция для проверки валидности пород
  const isPorodaValid = () => {
    return compositionItems.every(item => item.kodporodato && item.kodporodato !== '');
  };

  // Функция для проверки общей валидности состава
  const isCompositionValid = () => {
    return isDolyaSumValid() && isPorodaValid();
  };

  // Функция для расчета запаса сырорастущей древесины на выдел
  const calculateZapasSyroVydel = useCallback((yarus) => {
    
    if (!form1Data) {
      return '';
    }
    
    const zapasSyroGa = doubleOrNil(yarus.zapassirga);
    const ploshadVydel = mobilePlotArea(form1Data, dataType);

    if (zapasSyroGa > 0 && ploshadVydel > 0) {
      const result = zapasSyroGa * ploshadVydel;
      return result.toFixed(2);
    }
    return '';
  }, [form1Data, dataType]);

  // Функция для проверки валидности конкретного элемента состава
  const isCompositionItemValid = (item) => {
    return item.kodporodato && item.kodporodato !== '';
  };

  // Автоматическое обновление запаса сырорастущей древесины на выдел
  useEffect(() => {
    if (form1Data && yarusyDependent.length > 0) {
      const updatedYarusy = yarusyDependent.map(yarus => {
        const calculatedValue = calculateZapasSyroVydel(yarus);
        if (calculatedValue !== yarus.zapassir) {
          return { ...yarus, zapassir: calculatedValue };
        }
        return yarus;
      });
      
      // Проверяем, есть ли изменения
      const hasChanges = updatedYarusy.some((yarus, index) => 
        yarus.zapassir !== yarusyDependent[index]?.zapassir
      );
      
      if (hasChanges) {
        setYarusyDependent(updatedYarusy);
      }
    }
  }, [form1Data, yarusyDependent, calculateZapasSyroVydel, setYarusyDependent]);


  // Сохранение состава
  const saveComposition = () => {
    // Проверяем валидность долей перед сохранением
    if (!isDolyaSumValid()) {
      const currentSum = calculateDolyaSum();
      alert(`Ошибка: Сумма долей должна быть равна 10. Текущая сумма: ${currentSum}`);
      return;
    }

    // Проверяем валидность пород перед сохранением
    if (!isPorodaValid()) {
      alert('Ошибка: Все породы должны быть выбраны.');
      return;
    }

    const compositionString = compositionToString(compositionItems);
    if (currentYarusId) {
      // Вместе со строкой сохраняем выбранные записи справочника: из строки
      // «5К4Е1П» видно только буквы, а не то, какой кедр или ель выбраны.
      const sostavItems = compositionItems
        .filter(item => item.dolap && item.kodporodato)
        .map(item => ({ dolap: String(item.dolap), kodporodato: String(item.kodporodato) }));
      setYarusy(yarusy.map(y =>
        y.id === currentYarusId ? { ...y, sostav: compositionString, sostavItems } : y
      ));
      // Таблица характеристик пород пересоберётся автоматически (эффект на изменение состава)
    }
    closeCompositionModal();
  };

  // Автоматическое создание строк характеристик пород на основе состава
  const updatePorodaCharacteristicsFromComposition = (yarusId, compositionItems) => {
    const yarus = getCombinedYarusy().find(y => y.id === yarusId);
    if (!yarus) return;

    // Фильтруем только заполненные элементы состава
    const validItems = compositionItems.filter(item => item.dolap && item.kodporodato);
    
    // Если нет валидных элементов, не обновляем таблицу
    if (validItems.length === 0) {
      return;
    }
    
    // Сохраняем ID породы, а не название
    const handbook = handbooks.find(h => h.id === 31);
    const newCharacteristics = validItems.map((item, index) => {
      // kodporodato должен быть ID, а не названием
      let porodaId = item.kodporodato;
      
      // Если это строка (название), пытаемся найти ID по названию
      if (typeof porodaId === 'string' && handbook && handbook.references) {
        const selectedOption = handbook.references.find(r => 
          r.name === porodaId || r.name.toLowerCase() === porodaId.toLowerCase()
        );
        if (selectedOption) {
          porodaId = selectedOption.id.toString();
        }
      } else if (typeof porodaId === 'number') {
        // Если это число, преобразуем в строку для консистентности
        porodaId = porodaId.toString();
      }
      
      return {
        id: Date.now() + index + Math.random() * 1000, // Используем timestamp + index + random для уникальности
        yarus: yarus.yarus,
        yarusId: yarus.id, // Сохраняем ID яруса
        dolap: item.dolap,
        kodporodato: porodaId, // Сохраняем ID, а не название
        vozr: '',
        hsr: '',
        dsr: ''
      };
    });

    // Добавляем новые характеристики к существующим (не заменяем)
    const updatedCharacteristics = [...porodaCharacteristics, ...newCharacteristics];
    setPorodaCharacteristics(updatedCharacteristics);
    
    // Создаем соответствующие зависимые данные с теми же ID
    const newCharacteristicsDependent = validItems.map((item, index) => ({
      id: newCharacteristics[index].id, // Используем тот же ID что и в независимых данных
      yarus: yarus.yarus,     // Добавляем ярус
      dolap: item.dolap,      // Добавляем долю
      // Породу храним как ID — тем же значением, что и в независимых данных,
      // иначе Form4/Form5 не свяжут свою строку с этой (они сверяют kodporoda с kodporodato).
      kodporodato: newCharacteristics[index].kodporodato,
      vozr: '',
      hsr: '',
      dsr: ''
    }));
    const updatedCharacteristicsDependent = [...porodaCharacteristicsDependent, ...newCharacteristicsDependent];
    setPorodaCharacteristicsDependent(updatedCharacteristicsDependent);
  };


  // Функции для работы с календарем года
  const openYearPicker = () => {
    setShowYearPicker(true);
  };

  const closeYearPicker = () => {
    setShowYearPicker(false);
  };

  const selectYear = (year) => {
    const newData = { ...displayData, glu: year.toString() };
    setLocalFormData(newData);
    if (setFormData) {
      setFormData(newData);
    }
    closeYearPicker();
  };

  // Генерируем список годов (от 1900 до текущего года + 10)
  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear + 10; year >= 1900; year--) {
      years.push(year);
    }
    return years;
  };

  // Функции для работы с ярусами
  const addYarus = () => {
    // Ограничиваем количество ярусов до 3
    if (yarusy.length >= 3) {
      return;
    }
    
    const newId = Math.max(...yarusy.map(y => y.id), 0) + 1;
    const yarusNumber = yarusy.length + 1; // Автоматически определяем номер яруса (1, 2, 3)
    
    // Добавляем независимые поля
    const newYarus = {
      id: newId,
      yarus: yarusNumber.toString(), // Автоматически заполняем номер яруса
      sostav: ''
    };
    const newYarusy = [...yarusy, newYarus];
    setYarusy(newYarusy);
    
    // Добавляем зависимые поля
    const newYarusDependent = {
      id: newId,
      yarus: yarusNumber.toString(), // Автоматически заполняем номер яруса
      visota: '',
      klvozr: '',
      grvozr: '',
      polnota: '',
      zapassirga: '',
      zapassir: '',
      zapas_suhostoy: '',
      zapas_redin: '',
      zapas_ed_derev: '',
      zapas_zah_obsh: '',
      zapas_zah_likvid: ''
    };
    const newYarusyDependent = [...yarusyDependent, newYarusDependent];
    setYarusyDependent(newYarusyDependent);
    
    // Обновляем характеристики пород при добавлении яруса
    rebuildPorodaCharacteristicsFromAllYarusy();
  };

  const removeYarus = useCallback((id) => {
    // Удаляем из независимых данных
    const newYarusy = yarusy.filter(y => y.id !== id);
    
    // Перенумеровываем ярусы после удаления
    const renumberedYarusy = newYarusy.map((yarus, index) => ({
      ...yarus,
      yarus: (index + 1).toString() // Автоматически перенумеровываем: 1, 2, 3
    }));
    setYarusy(renumberedYarusy);
    
    // Удаляем из зависимых данных
    const newYarusyDependent = yarusyDependent.filter(y => y.id !== id);
    
    // Перенумеровываем зависимые данные ярусов
    const renumberedYarusyDependent = newYarusyDependent.map((yarus, index) => ({
      ...yarus,
      yarus: (index + 1).toString() // Автоматически перенумеровываем: 1, 2, 3
    }));
    setYarusyDependent(renumberedYarusyDependent);
    
    // Удаляем характеристики пород, связанные с удаляемым ярусом
    const newCharacteristics = porodaCharacteristics.filter(char => char.yarusId !== id);
    
    // Обновляем номера ярусов в оставшихся характеристиках
    const updatedCharacteristics = newCharacteristics.map((char) => {
      // Находим соответствующий ярус в перенумерованных данных
      const correspondingYarus = renumberedYarusy.find(y => y.id === char.yarusId);
      if (correspondingYarus) {
        return {
          ...char,
          yarus: correspondingYarus.yarus // Обновляем номер яруса
        };
      }
      return char;
    });
    
    setPorodaCharacteristics(updatedCharacteristics);
    
    // Обновляем зависимые данные характеристик пород
    const updatedCharacteristicsDependent = porodaCharacteristicsDependent
      .filter(char => updatedCharacteristics.some(indep => indep.id === char.id))
      .map((char) => {
        // Находим соответствующую характеристику
        const correspondingChar = updatedCharacteristics.find(indep => indep.id === char.id);
        if (correspondingChar) {
          return {
            ...char,
            yarus: correspondingChar.yarus // Обновляем номер яруса
          };
        }
        return char;
      });
    
    setPorodaCharacteristicsDependent(updatedCharacteristicsDependent);
    
    // Обновляем formData после удаления яруса
    const updatedFormData = {
      ...formData,
      yarusy: renumberedYarusy,
      yarusyDependent: renumberedYarusyDependent,
      porodaCharacteristics: updatedCharacteristics,
      poroda_characteristics_dependent: updatedCharacteristicsDependent
    };
    setFormData(updatedFormData);
  }, [yarusy, yarusyDependent, porodaCharacteristics, porodaCharacteristicsDependent, formData, setFormData, setYarusy, setYarusyDependent, setPorodaCharacteristics, setPorodaCharacteristicsDependent]);

  const updateYarus = (id, field, value) => {
    // Запрещаем изменение поля yarus (номер яруса)
    if (field === 'yarus') {
      return;
    }
    
    // Определяем, к какой группе полей относится поле
    const dependentFields = [
      'visota', 'klvozr', 'grvozr', 'polnota',
      'zapassirga', 'zapassir', 'zapassuh', 'zapasred',
      'zapased', 'zapaszahl', 'zapaszahllik'
    ];
    
    if (dependentFields.includes(field)) {
      // Валидация для запасов захламления: zapaszahl должен быть >= zapaszahllik
      if (field === 'zapaszahl' || field === 'zapaszahllik') {
        const currentYarus = yarusyDependent.find(y => y.id === id);
        if (currentYarus) {
          // Парсим значения, учитывая пустые строки
          const parseValue = (val) => {
            if (!val || val === '' || val === null || val === undefined) return null;
            const parsed = parseFloat(val);
            return isNaN(parsed) ? null : parsed;
          };
          
          const zapaszahlValue = field === 'zapaszahl' ? parseValue(value) : parseValue(currentYarus.zapaszahl);
          const zapaszahllikValue = field === 'zapaszahllik' ? parseValue(value) : parseValue(currentYarus.zapaszahllik);
          
          // Если оба значения заполнены, проверяем условие
          if (zapaszahlValue !== null && zapaszahllikValue !== null) {
            // Если обновляется zapaszahl и оно меньше zapaszahllik, корректируем zapaszahllik
            if (field === 'zapaszahl' && zapaszahlValue < zapaszahllikValue) {
              const formattedValue = parseFloat(value).toFixed(2);
              const newYarusyDependent = yarusyDependent.map(y => 
                y.id === id ? { ...y, [field]: value, zapaszahllik: formattedValue } : y
              );
              setYarusyDependent(newYarusyDependent);
              return;
            }
            
            // Если обновляется zapaszahllik и оно больше zapaszahl, корректируем zapaszahl
            if (field === 'zapaszahllik' && zapaszahllikValue > zapaszahlValue) {
              const formattedValue = parseFloat(value).toFixed(2);
              const newYarusyDependent = yarusyDependent.map(y => 
                y.id === id ? { ...y, [field]: value, zapaszahl: formattedValue } : y
              );
              setYarusyDependent(newYarusyDependent);
              return;
            }
          }
        }
      }
      
      // Обновляем зависимые данные
      const newYarusyDependent = yarusyDependent.map(y => 
        y.id === id ? { ...y, [field]: value } : y
      );
      setYarusyDependent(newYarusyDependent);
    } else {
      // Обновляем независимые данные
      const newYarusy = yarusy.map(y => 
        y.id === id ? { ...y, [field]: value } : y
      );
      setYarusy(newYarusy);
      
      // Если изменился ярус, обновляем ярус в зависимых данных и характеристиках пород
      if (field === 'yarus') {
        const newYarusyDependent = yarusyDependent.map(y => 
          y.id === id ? { ...y, yarus: value } : y
        );
        setYarusyDependent(newYarusyDependent);
        updateYarusInCharacteristics(id, value);
      }
      
      // Если изменился состав насаждения, пересборка произойдет автоматически через useEffect
    }
    
    // Сбрасываем флаг после обновления
    setTimeout(() => {
      isUpdatingFromForm.current = false;
    }, 0);
  };


  const updatePorodaCharacteristic = (id, field, value) => {
    // Определяем, к какой группе полей относится поле
    const dependentFields = ['vozr', 'hsr', 'dsr'];

    if (dependentFields.includes(field)) {
      // Обновляем зависимые данные. Поле хранится под тем же именем, под каким
      // уйдёт в API, — никаких параллельных копий значения в стейте нет.
      const newCharacteristicsDependent = porodaCharacteristicsDependent.map(char =>
        char.id === id ? { ...char, [field]: value } : char
      );
      setPorodaCharacteristicsDependent(newCharacteristicsDependent);
    } else {
      // Обновляем независимые данные
      const newCharacteristics = porodaCharacteristics.map(char => 
        char.id === id ? { ...char, [field]: value } : char
      );
      setPorodaCharacteristics(newCharacteristics);
    }
  };

  // Обновление яруса в характеристиках пород
  const updateYarusInCharacteristics = (yarusId, newYarusValue) => {
    const newCharacteristics = porodaCharacteristics.map(char => 
      char.yarusId === yarusId ? { ...char, yarus: newYarusValue } : char
    );
    setPorodaCharacteristics(newCharacteristics);
  };


  return (
    <>
      <div className="template-section">
      <div 
        className="template-header" 
        style={{ cursor: 'pointer', padding: '10px', backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <h3>
          {dataType === 'actual' 
            ? 'II. Форма ввода фактических данных' 
            : 'II. Форма ввода таксационной информации'
          }
        </h3>
      </div>
      
      <div className="template-content">
        {/* Информация */}
        <InfoSection
          displayData={displayData}
          setLocalFormData={setLocalFormData}
          setFormData={setFormData}
          handbooks={handbooks}
          isFieldsLocked={isFieldsLocked}
          openPicker={openPicker}
          openYearPicker={openYearPicker}
          clearTableData={clearTableData}
          infoTableScrollRef={infoTableScrollRef}
          infoTableScrollLeft={infoTableScrollLeft}
          handleInfoTableScroll={handleInfoTableScroll}
        />

        {/* Характеристика насаждения общая */}
        <CharacteristicsSection
          displayData={displayData}
          setLocalFormData={setLocalFormData}
          setFormData={setFormData}
          handbooks={handbooks}
          isFieldsLocked={isFieldsLocked}
          openPicker={openPicker}
          clearTableData={clearTableData}
        />

        {/* Характеристика насаждения по ярусам. На землях, не покрытых лесной
            растительностью, таблица необязательна целиком. */}
        <YarusySection
          tiersOptional={isNonForestLandCategory(displayData.katzem, handbooks)}
          dataType={dataType}
          isFieldsLocked={isFieldsLocked}
          yarusy={yarusy}
          getCombinedYarusy={getCombinedYarusy}
          handbooks={handbooks}
          openPicker={openPicker}
          openCompositionModal={openCompositionModal}
          updateYarus={updateYarus}
          addYarus={addYarus}
          removeYarus={removeYarus}
          calculateZapasSyroVydel={calculateZapasSyroVydel}
          tableScrollRef={tableScrollRef}
          tableScrollPosition={tableScrollPosition}
          handleSliderChange={handleSliderChange}
          handleTableScroll={handleTableScroll}
        />

        {/* Характеристика породы */}
        <PorodaCharacteristicsSection
          isFieldsLocked={isFieldsLocked}
          getCombinedPorodaCharacteristics={getCombinedPorodaCharacteristics}
          updatePorodaCharacteristic={updatePorodaCharacteristic}
          handbooks={handbooks}
        />
      </div>

      {/* Picker для справочников */}
      <PickerModal
        showPicker={showPicker}
        pickerSearch={pickerSearch}
        setPickerSearch={setPickerSearch}
        getFilteredOptions={getFilteredOptions}
        getSelectedIndexInFiltered={getSelectedIndexInFiltered}
        pickerValue={pickerValue}
        selectPickerValue={selectPickerValue}
        closePicker={closePicker}
      />

      {/* Календарь для выбора года */}
      <YearPickerModal
        showYearPicker={showYearPicker}
        displayData={displayData}
        selectYear={selectYear}
        closeYearPicker={closeYearPicker}
      />

      {/* Модальное окно для редактирования состава насаждения */}
      <CompositionModal
        showCompositionModal={showCompositionModal}
        compositionItems={compositionItems}
        handbooks={handbooks}
        isFieldsLocked={isFieldsLocked}
        calculateDolyaSum={calculateDolyaSum}
        isDolyaSumValid={isDolyaSumValid}
        isPorodaValid={isPorodaValid}
        isCompositionValid={isCompositionValid}
        isCompositionItemValid={isCompositionItemValid}
        updateCompositionItem={updateCompositionItem}
        addCompositionItem={addCompositionItem}
        removeCompositionItem={removeCompositionItem}
        openPicker={openPicker}
        saveComposition={saveComposition}
        closeCompositionModal={closeCompositionModal}
      />

      {/* Уведомление */}
      {showNotification && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          background: '#dc3545',
          color: 'white',
          padding: '15px 20px',
          borderRadius: '5px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          zIndex: 9999,
          maxWidth: '400px',
          fontSize: '14px'
        }}>
          {notificationMessage}
        </div>
      )}
      </div>
    </>
  );
};

export default Form2;
